import {
  Cog6ToothIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Menu, ShieldCheck, Store } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ConfirmationModal from './ConfirmationModal';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import NotificationModal from './NotificationModal';
import SettingsModal from './SettingsModal';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth, useUserRole, useCustomerData } from '../../hooks/useAuth';
import axiosInstance from '../../services/axiosInstance';
import { authStorage } from '../../utils/authStorage';
import { useTheme } from './ThemeProvider';
import { resolveAssetUrl, withAssetVersion } from '../../utils/assetUrl';
import { setCustomerData } from '../../store/authSlice';

export default function Header({ allowedRoles = [], toggleSidebar, brandSnapshot = null }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const dropdownRef = useRef();
  const profileButtonRef = useRef();
  const location = useLocation();
  const { user, logout, customerId } = useAuth();
  const dispatch = useDispatch();
  const role = useUserRole();
  const getCustomerData = useCustomerData();
  const customerData = useSelector((state) => state.auth.customer_data);
  const { currentTheme } = useTheme();
  // Notification logic is now handled by NotificationContext

  // Get customer info for admin, POS, and cashier
  const customer = customerData || ((role === 1 || role === 2) && customerId ? getCustomerData() : null);

  // Keep logo in state so it can update when superadmin changes it
  const [logoUrl, setLogoUrl] = useState(resolveAssetUrl(customer?.logo));

  // Map numeric user_type to role label
  const roleLabels = { 0: 'Super Admin', 1: 'Admin' };
  const displayName = user?.username || 'Unknown User';
  const roleLabel = user?.user_type === 2
    ? (user?.role_name || 'User')
    : (roleLabels[user?.user_type] || 'Unknown Role');
  const unreadCount = notifications.filter(n => !n.read).length;
  const roleMeta = {
    0: {
      label: 'Super Admin Workspace',
      subtitle: 'Platform controls and customer management',
      icon: ShieldCheck,
    },
    1: {
      label: 'Admin Workspace',
      subtitle: 'Store operations and business overview',
      icon: Store,
    },
    2: {
      label: 'Point of Sale',
      subtitle: 'Daily branch transactions and activity',
      icon: Store,
    },
    3: {
      label: 'Point of Sale',
      subtitle: 'Daily branch transactions and activity',
      icon: Store,
    },
  };
  const routeRole =
    location.pathname.startsWith('/superadmin')
      ? 0
      : location.pathname.startsWith('/admin')
        ? 1
        : (role === 1 || role === 0 ? role : 2);
  const activeMeta = roleMeta[routeRole] || roleMeta[1];
  const ActiveMetaIcon = activeMeta.icon;

  // Logout handler
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Short helper: fetch latest customer data and update logo/cache for Admins
  const refreshCustomerLogo = useCallback(async () => {
    if ((role !== 1 && role !== 2) || !customerId) return;

    try {
      const res = await axiosInstance.get("/superadmin/customers");
      const list = Array.isArray(res.data) ? res.data : (res.data?.customers || []);
      const found = list.find((c) => String(c.id) === String(customerId));
      if (found) {
        // Use authStorage.setCustomerData to ensure only one customer_* key exists
        authStorage.setCustomerData(customerId, found);
        dispatch(setCustomerData(found));
        setLogoUrl(withAssetVersion(found.logo, found.updated_at ? new Date(found.updated_at).getTime() : undefined));
      }
    } catch (e) {
      console.debug("Header: failed to refresh customer logo", e?.message || e);
    }
  }, [role, customerId, dispatch]);

  // Redirect if user not logged in or role not allowed
  useEffect(() => {
    if (!user) {
      logout();
    } else if (allowedRoles.length && !allowedRoles.includes(user._type)) {
      alert('You are not authorized to access this page.');
      logout();
    }
  }, [user, allowedRoles, logout]);

  // Fetch fresh customer data (admins only) to reflect latest logo updates
  useEffect(() => {
    refreshCustomerLogo();
  }, [refreshCustomerLogo]);

  useEffect(() => {
    const handleBrandUpdated = (event) => {
      const updatedCustomer = event.detail;
      if (!updatedCustomer) return;
      if (updatedCustomer.logo && updatedCustomer.previewUrl) {
        setLogoUrl(updatedCustomer.previewUrl);
        refreshCustomerLogo();
      } else if (updatedCustomer.logo) {
        setLogoUrl(withAssetVersion(
          updatedCustomer.logo,
          updatedCustomer.updated_at ? new Date(updatedCustomer.updated_at).getTime() : Date.now()
        ));
        refreshCustomerLogo();
      } else if (updatedCustomer.logo === null) {
        setLogoUrl(null);
      }
    };

    window.addEventListener("customer-brand-updated", handleBrandUpdated);
    return () => window.removeEventListener("customer-brand-updated", handleBrandUpdated);
  }, [refreshCustomerLogo]);

  useEffect(() => {
    if (brandSnapshot) return;
    setLogoUrl(resolveAssetUrl(customer?.logo));
  }, [customer?.logo, brandSnapshot]);

  useEffect(() => {
    const handleUserProfileUpdated = () => {
      setShowDropdown(false);
    };

    window.addEventListener("user-profile-updated", handleUserProfileUpdated);
    return () => window.removeEventListener("user-profile-updated", handleUserProfileUpdated);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Branding is shown in the Sidebar. Header stays clean with profile and actions only.
  const fallbackLogo = `${process.env.PUBLIC_URL || ""}/logo.svg`;
  const hasLiveBrandSnapshot = brandSnapshot !== null && brandSnapshot !== undefined;
  const liveBrandLogo = brandSnapshot?.logo === null
    ? null
    : brandSnapshot?.previewUrl
      ? brandSnapshot.previewUrl
      : brandSnapshot?.logo
        ? withAssetVersion(
          brandSnapshot.logo,
          brandSnapshot.updated_at ? new Date(brandSnapshot.updated_at).getTime() : Date.now()
        )
        : null;
  const displayedLogo = hasLiveBrandSnapshot ? liveBrandLogo : logoUrl;
  const dropdownRect = profileButtonRef.current?.getBoundingClientRect();
  const dropdownTop = dropdownRect ? dropdownRect.bottom + 12 : 76;
  const dropdownRight = dropdownRect ? window.innerWidth - dropdownRect.right : 24;

  return (
    <>
      <header
        className="relative overflow-hidden border-b border-slate-200/70 px-2 py-2.5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur-xl sm:px-6 sm:py-3"
        style={{
          background: `linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.94) 52%, ${currentTheme.color}10 100%)`,
        }}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.7),_transparent_32%),radial-gradient(circle_at_85%_20%,_rgba(255,255,255,0.55),_transparent_26%)]" />
          <div
            className="absolute -top-10 left-24 h-24 w-24 rounded-full blur-3xl"
            style={{ backgroundColor: `${currentTheme.color}18` }}
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />
        <div className="relative flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:bg-slate-100 focus:outline-none lg:hidden"
              aria-label="Open sidebar"
              onClick={toggleSidebar}
            >
              <Menu size={22} strokeWidth={2.2} />
            </button>

            <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 px-2 py-1.5 shadow-sm sm:gap-3 sm:px-3 sm:py-2">
              {(role === 1 || role === 2 || role === 3) ? (
                <div className="flex h-10 w-10 items-center justify-center overflow-hidden sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-white sm:shadow-sm sm:h-12 sm:w-12">
                  <img
                    key={displayedLogo ? displayedLogo : fallbackLogo}
                    src={displayedLogo ? displayedLogo : fallbackLogo}
                    alt={customer?.name ? `${customer.name} logo` : 'Zero One logo'}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-2xl text-white shadow-sm sm:h-12 sm:w-12"
                  style={{ background: currentTheme.color }}
                >
                  <ShieldCheck size={20} />
                </div>
              )}

              <div className="hidden min-w-0 sm:block">
                <div className="flex items-center gap-2">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full text-white sm:h-7 sm:w-7"
                    style={{ background: currentTheme.color }}
                  >
                    <ActiveMetaIcon size={14} />
                  </span>
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {activeMeta.label}
                  </p>
                </div>
                <p className="hidden truncate text-xs text-slate-500 sm:block">
                  {activeMeta.subtitle}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {role === 1 && (
              <div className="relative">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-1 shadow-sm transition hover:bg-slate-100 sm:p-1.5">
                  <NotificationBell count={unreadCount} onClick={() => setNotifDropdownOpen((prev) => !prev)} />
                </div>
                {notifDropdownOpen && (
                  <NotificationModal
                    open={notifDropdownOpen}
                    onClose={() => setNotifDropdownOpen(false)}
                    items={notifications}
                    onMarkAsRead={markAsRead}
                    onMarkAllAsRead={markAllAsRead}
                  />
                )}
              </div>
            )}

            <div className="relative" ref={dropdownRef}>
              <button
                ref={profileButtonRef}
                onClick={() => setShowDropdown((prev) => !prev)}
                className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-2 py-1.5 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:gap-3 sm:px-3 sm:py-2"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  {user?.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="Profile"
                      className="h-9 w-9 rounded-2xl object-cover ring-2 ring-slate-100 sm:h-10 sm:w-10"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 sm:h-10 sm:w-10">
                      <UserCircleIcon className="h-6 w-6" />
                    </div>
                  )}

                  <div className="hidden text-left sm:block">
                    <p className="max-w-[160px] truncate text-sm font-semibold text-slate-900">
                      {displayName}
                    </p>
                    <p className="text-xs text-slate-500">{roleLabel}</p>
                  </div>
                </div>
                <ChevronDownIcon className={`h-4 w-4 text-slate-500 transition ${showDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {showDropdown && createPortal(
        <>
          <div
            className="fixed inset-0 z-[55]"
            onClick={() => setShowDropdown(false)}
          />
          <div
            className="fixed z-[60] w-60 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_45px_rgba(15,23,42,0.14)]"
            style={{ top: dropdownTop, right: dropdownRight }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
              <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
              <p className="text-xs text-slate-500">{roleLabel}</p>
            </div>
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowProfileModal(true);
              }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <UserCircleIcon className="h-5 w-5 text-slate-500" />
              Profile
            </button>
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowSettingsModal(true);
              }}
              className="flex w-full items-center gap-3 px-4 py-3 text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <Cog6ToothIcon className="h-5 w-5 text-slate-500" />
              Settings
            </button>
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowLogoutModal(true);
              }}
              className="flex w-full items-center gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-700 transition hover:bg-rose-50 hover:text-rose-700"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 text-slate-500" />
              Logout
            </button>
          </div>
        </>,
        document.body
      )}

      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        type="delete"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleLogout();
            setShowLogoutModal(false);
          }
        }}
      />

      {/* Profile Modal */}
      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        displayName={displayName}
        roleLabel={roleLabel}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onBrandUpdated={(nextBrand) => {
          if (nextBrand?.logo === null) {
            setLogoUrl(null);
          }
        }}
      />
    </>
  );
}
